import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { chatService, Thread } from '../services/chatService';
import { useAuth } from './AuthContext';
import type { ServiceTopicRoute } from '../config/serviceTopicRoutes';

export type PendingChatTopic = ServiceTopicRoute & {
  /** When true, first message creates a new thread instead of reusing the open topic thread. */
  forceNewThread?: boolean;
  /** Prefill the composer. Used when a vehicle listing opens chat. */
  draftMessage?: string;
  /** Stock number used to reopen the same car thread instead of a generic Buy a Vehicle thread. */
  vehicleStock?: string;
  vehicleId?: string;
};

interface ChatContextType {
  threads: Thread[];
  selectedThread: Thread | null;
  setSelectedThread: (thread: Thread | null) => void;
  loadingThreads: boolean;
  refreshThreads: () => Promise<void>;
  markThreadRead: (threadId: string) => Promise<void>;
  startNewChat: () => void;
  exitNewChatMode: () => void;
  startTopicChat: (topic: PendingChatTopic) => void;
  pendingTopic: PendingChatTopic | null;
  clearPendingTopic: () => void;
  isNewChatMode: boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [isNewChatMode, setIsNewChatMode] = useState(false);
  const [pendingTopic, setPendingTopic] = useState<PendingChatTopic | null>(null);

  const refreshThreads = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingThreads(true);
      const threadsData = await chatService.getThreads();
      setThreads(threadsData);
    } catch (error) {
      console.error('Failed to load threads:', error);
    } finally {
      setLoadingThreads(false);
    }
  }, [isAuthenticated]);

  const markThreadRead = useCallback(async (threadId: string) => {
    try {
      await chatService.markMessagesAsRead(threadId);
      setThreads((prev) =>
        prev.map((t) => (t.thread_id === threadId || t._id === threadId ? { ...t, unread_count: 0 } : t))
      );
      setSelectedThread((prev) =>
        prev?.thread_id === threadId || prev?._id === threadId ? { ...prev, unread_count: 0 } : prev
      );
    } catch (error) {
      console.error('Failed to mark thread as read:', error);
    }
  }, []);

  // Clear selected thread to show "start new chat" UI
  const clearPendingTopic = useCallback(() => {
    setPendingTopic(null);
  }, []);

  const startNewChat = useCallback(() => {
    setPendingTopic(null);
    setSelectedThread(null);
    setIsNewChatMode(true);
  }, []);

  const exitNewChatMode = useCallback(() => {
    setIsNewChatMode(false);
    setPendingTopic(null);
  }, []);

  const startTopicChat = useCallback((topic: PendingChatTopic) => {
    setPendingTopic(topic);
    setSelectedThread(null);
    setIsNewChatMode(true);
  }, []);

  // Custom handler to select thread and exit new chat mode
  const handleSetSelectedThread = useCallback((thread: Thread | null) => {
    setSelectedThread(thread);
    if (thread) {
      setIsNewChatMode(false);
    }
  }, []);

  // Load threads when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshThreads();
    } else {
      setThreads([]);
      setSelectedThread(null);
    }
  }, [isAuthenticated, refreshThreads]);

  // Listen for thread updates
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = chatService.onThreadUpdate((updatedThread) => {
      setThreads((prev) => {
        const index = prev.findIndex((t) => t._id === updatedThread._id);
        if (index >= 0) {
          const newThreads = [...prev];
          newThreads[index] = updatedThread;
          return newThreads;
        }
        return [updatedThread, ...prev];
      });
    });

    return unsubscribe;
  }, [isAuthenticated]);

  return (
    <ChatContext.Provider
      value={{
        threads,
        selectedThread,
        setSelectedThread: handleSetSelectedThread,
        loadingThreads,
        refreshThreads,
        markThreadRead,
        startNewChat,
        exitNewChatMode,
        startTopicChat,
        pendingTopic,
        clearPendingTopic,
        isNewChatMode,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
